/* eslint-disable unicorn/no-null */
import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Num, Obj, Str } from '@livecontrol/core-utils';
import { Account, Destination, Event, Location, Slide } from '@livecontrol/scheduler/model';
import { EventValidator } from '@livecontrol/scheduler/validator';
import { useCallback, useState } from 'react';
import type { PartialDeep } from 'type-fest';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';
import * as DbxGet from './dbx';
import * as Dbx from './dbx-edit';
import { QUERY } from './use-events';

interface TVariables {
  event: Omit<Dbx.Record, 'id' | 'location'> & { id: string; location?: string };
}

interface TData {
  updateEvent: Dbx.Record;
}

interface Args {
  event: Event.Like;
  eventLocation?: Location;
  account: Account.Like;

  replacement: Partial<
    Omit<Event, 'id' | 'location'> & {
      location: Location.Like;
      simulatedLiveDetails?: {
        assetId?: number;
        fileName?: string;
        assetUrl?: string;
      };
    }
  >;
}

const MUTATION = gql`
  mutation EditEvent($event: EventUpdateInput!) {
    updateEvent(event: $event) {
      ...StandardEventResponse
    }
  }
  ${DbxGet.StandardEventResponse}
`;

export const useEdit = (): [
  (args: Args) => Promise<Event | undefined>,
  MutationResult<Event, 'event'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [event, setEvent] = useState<Event | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Event | undefined> => {
        let event_: Event | undefined;

        try {
          const { replacement } = args;

          // Parse the input arguments
          const required = {
            id: Event.toId(args.event)?.toString() // @fixme - API type mismatch
          };

          if (!Obj.isHydrated(required)) {
            throw Errors.badRequest();
          }

          let technical_contact_id: number | null | undefined = Num.normalize(
            replacement.technicalContactSettings?.contactId
          );

          if (
            !(
              technical_contact_id &&
              technical_contact_id > 0 &&
              replacement.technicalContactSettings?.enabled
            )
          ) {
            technical_contact_id = null;
          }

          let contact_id: number | null | undefined = Num.normalize(
            replacement.contactSettings?.contactId
          );

          if (
            !(
              contact_id &&
              contact_id > 0 &&
              replacement.contactSettings?.enabled
            )
          ) {
            contact_id = null;
          }

          let sync_contact_id: number | null | undefined = Num.normalize(
            replacement.syncContactSettings?.contactId
          );

          if (
            !(
              sync_contact_id &&
              sync_contact_id > 0 &&
              replacement.syncContactSettings?.enabled
            )
          ) {
            sync_contact_id = null;
          }

          const variables: TVariables = {
            event: {
              ...required,
              name: Str.normalize(replacement.title),
              is_operated: replacement.production === Event.Production.Produced,
              is_simulated_live: replacement.production === Event.Production.Simulated_Live,
              /*
               * is_mobile: replacement.production === Event.Production.Mobile,
               * is_produced: replacement.production === Event.Production.Produced,
               * is_static:replacement.production === Event.Production.Static,
               */
              is_mobile_kit: replacement.production === Event.Production.Mobile_Kit,
              is_mobile: replacement.production === Event.Production.Mobile,
              is_external_encoder: replacement.production === Event.Production.External,
              // event_category: Enum.normalize(Event.EventCategories, replacement.production), // 'EXTERNAL_ENCODER'
              event_category: ((): string | undefined => {
                if (replacement.production === Event.Production.Mobile_Kit) {
                  return EventValidator.EventCategories.CATEGORIES.MobileKit;
                }

                if (replacement.production === Event.Production.Mobile) {
                  return EventValidator.EventCategories.CATEGORIES.Mobile;
                }

                if (replacement.production === Event.Production.External) {
                  return EventValidator.EventCategories.CATEGORIES.External;
                }

                if (replacement.production === Event.Production.Produced) {
                  return EventValidator.EventCategories.CATEGORIES.Produced;
                }

                if (replacement.production === Event.Production.Simulated_Live) {
                  return EventValidator.EventCategories.CATEGORIES.Simulated;
                }

                if (replacement.production === Event.Production.Static) {
                  return EventValidator.EventCategories.CATEGORIES.Static;
                }

                if (replacement.production === Event.Production.Test) {
                  return EventValidator.EventCategories.CATEGORIES.Test;
                }

                return undefined;
              })(),
              status: replacement.phase?.toString(),
              start_time: replacement.start?.toISO(),
              end_time: replacement.end?.toISO(),
              location: Location.toId(replacement.location),
              stream_youtube: !!replacement.destinations?.find(
                (destination) => destination.service === Destination.Service.YouTube
              ),
              stream_facebook: !!replacement.destinations?.find(
                (destination) => destination.service === Destination.Service.Facebook
              ),
              hidden: !replacement.destinations?.length,
              record_only: false,
              stream_live: true,
              operator_notes: replacement.operatorNotes,
              description: replacement.description,
              background_url: Str.normalize(replacement.backgroundURL) ?? null,
              stream_zoom: replacement.zoomDetails?.streamZoom,
              zoom_meeting_id: replacement.zoomDetails?.meetingId,
              zoom_password: replacement.zoomDetails?.password,
              simulated_live_asset_id: replacement.simulatedLiveDetails?.assetId,
              simulated_live_filename: replacement.simulatedLiveDetails?.fileName,
              asset_url: replacement.simulatedLiveDetails?.assetUrl,
              event_password:
                !replacement.destinations || replacement.destinations.length === 0
                  ? ''
                  : Str.normalize(replacement.password) ?? null,
              registration: replacement.registration,
              require_registration: replacement.registration
                ? replacement.requireRegistration
                : false,
              guestbook: replacement.showGuestbook,
              live_chat: replacement.liveChat,
              show_viewers:
                replacement.registration || replacement.liveChat ? replacement.showViewers : false,
              redirect_url: replacement.redirectUrl ?? '',
              cue_sheet_url: Str.normalize(replacement.cueSheetURL) ?? null,
              audio_cue_sheet_url: Str.normalize(replacement.audioCueSheetURL) ?? null,
              receive_iso_recordings: replacement.receiveIsoRecordings
                ? replacement.receiveIsoRecordings
                : false,
              use_intro_and_outro_slides: replacement.useIntroAndOutroSlides
                ? replacement.useIntroAndOutroSlides
                : false,
              client_booking_customer_name: replacement.clientContactDetails?.name === null
                ? null
                : Str.normalize(replacement.clientContactDetails?.name),
              client_booking_customer_email: replacement.clientContactDetails?.email === null
                ? null
                : Str.normalize(replacement.clientContactDetails?.email),
              client_booking_customer_phone: replacement.clientContactDetails?.phoneNumber === null
                ? null
                : Str.normalize(replacement.clientContactDetails?.phoneNumber),
              client_contacts: replacement.clientContacts?.map(contact => ({
                client_booking_customer_name: Str.normalize(contact.name),
                client_booking_customer_email: Str.normalize(contact.email),
                client_booking_customer_phone: Str.normalize(contact.phoneNumber)
              })),
              type: <string | undefined>undefined,
              technical_contact_id,
              contact_id,
              sync_contact_id,
              automatic_zoom: replacement.automaticZoom,
              slides: replacement.useIntroAndOutroSlides ? 
                          replacement.slides?.map(
                            (
                              slide: Slide
                            ): {
                              slide_type: string;
                              file_name: string;
                              url: string;
                              duration?: number;
                              size?: number;
                              file_type?: string;
                            } => ({
                              slide_type: slide.slide_type ?? 'intro',
                              file_name: slide.file_name,
                              url: slide.url,
                              duration: slide.duration,
                              size: slide.size,
                              file_type: slide.file_type
                            })
                          ) ?? [] : [],
              integrations:
                replacement.destinations
                  ?.map(
                    (
                      destination: PartialDeep<Destination> | undefined
                    ):
                      | {
                          integration: string;
                          type: string;
                        }
                      | undefined =>
                      destination?.id && destination.service
                        ? {
                            integration: destination.id,
                            type:
                              destination.service === Destination.Service.LiveControl
                                ? 'livecontrol'
                                : destination.service === Destination.Service.Facebook
                                ? 'facebook'
                                : destination.service === Destination.Service.YouTube
                                ? 'youtube'
                                : 'rtmp'
                          }
                        : undefined
                  )
                  .filter(
                    (destination) =>
                      !!destination?.integration && destination.type !== 'livecontrol'
                  ) ?? [],
              extra_charges: replacement.extraCharges?.map((charge) => ({
                type: charge.type,
                amount: charge.amount
              })).filter((charge) => charge.amount > 0) ?? []
            }
          };

          if (!variables.event.hidden) {
            variables.event.was_ever_public = true;
          }

          variables.event.type =
            replacement.production === Event.Production.Mobile
              ? 'mobile'
              : replacement.production === Event.Production.Test
              ? 'test'
              : 'regular';

          if (args.eventLocation?.simulatedLive) {
            variables.event.is_simulated_live = true;
          } else if (!args.eventLocation?.simulatedLive) {
            variables.event.is_simulated_live = false;
            delete variables.event.simulated_live_filename;
            delete variables.event.simulated_live_asset_id;
            delete variables.event.asset_url;
          }

          if (variables.event.name && replacement.production === Event.Production.Test) {
            variables.event.name = `Test - ${variables.event.name}`;
          }

          // Execute the GraphQL mutation
          const accountId = Account.toId(args.account)!;

          const response = await mutation({
            variables,
            refetchQueries: [
              {
                query: QUERY,
                variables: {
                  clientAsFloat: accountId,
                  clientAsInt: accountId,
                  clientAsStr: accountId.toString()
                }
              }
            ]
          })
            .then(({ data }: FetchResult<TData>) => data?.updateEvent)
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          // Parse the server response
          event_ = Dbx.normalize(response);

          if (!event_) {
            throw Errors.serverError();
          }
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setEvent(event_);

        return event_;
      },
      [mutation, setError, setEvent]
    ),
    {
      event,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
